import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { Program, Enrollment, getEnrollmentPaymentStatus, Invoice, getProgramTypeIcon, parseCron, getScheduleTimeRange } from '../types/program';
import { useAuth } from '../contexts/AuthContext';
import { Navigate, Link, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { ChildForm } from '../components/AddChildForm';
import {
  UserPlus, Edit, Trash2, AlertCircle, FileText, Users,
  CalendarClock, DoorOpen, GraduationCap, School,
  Phone, ChevronLeft, ChevronRight,
  Plus, X as XIcon,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { calculateAge, formatDateDisplay } from '../utils/date';
import { grades } from '../config/schools';
import settings from '../config/settings.json';
import { trackEvent } from '../utils/analytics';
import { isBhcsStudentWaiverSigned, isBhcsStudentWaiverOptOut, readWaiverContacts, BhcsStudentWaiver, BhcsWaiverMetadata } from '../types/waiver';
import { WaiverPrintPreview } from '../components/WaiverPrintPreview';
import { CoParentInviteModal } from '../components/CoParentInviteModal';
import { StudentContactsEditor } from '../components/StudentContactsEditor';
import moment from 'moment';
import { useLabels } from '../config/useLabels';
import {
  useAttendanceByStudents,
  useAuthorizedPickups,
  useAddAuthorizedPickup,
  useRemoveAuthorizedPickup,
} from '../hooks/useAttendance';
import { getTodaySessionDate } from '../utils/schedule';
import { buildTodaySessions } from '../utils/todaySessions';
import { ParentAttendancePill, ParentAttendanceTimes } from '../components/attendance/ParentAttendancePill';
import { solidTintFor } from '../components/StudentAvatar';
import { TYPE_CLOCK, TYPE_OBJECT, TYPE_SECTION } from '../components/typeScale';
import type { AuthorizedPickup } from '../types/attendance';
import { BrandBadge } from '../components/BrandBadge';
import { useCartHeldInvoiceIds } from '../hooks/useCartHeldInvoiceIds';

// ─── types ────────────────────────────────────────────────────────────────────

interface ParentInfo {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

interface StudentParentRow {
  parent_id: string;
  relationship: string;
  is_primary: boolean;
  can_pay: boolean;
  /**
   * Null when RLS lets us read the link row but not the parent behind it — the embed
   * resolves to `null` rather than dropping the row. Real for a dual-role user: a teacher
   * reading their own child's page also matches the staff policy on `student_parents`.
   */
  parents: ParentInfo | null;
}

interface Student {
  id: string;
  first_name: string;
  last_name: string;
  date_of_birth: string | null;
  metadata: {
    school?: string;
    grade?: string;
    /** Digits-only classroom number; kept as a string so "013" survives. */
    room_number?: string;
    deposit_badge?: { amount: number };
    bhcs_waiver?: unknown;
  };
  active: boolean;
  created_at: string;
  updated_at: string;
  added_by: { id: string; first_name: string; last_name: string } | null;
}

interface EnrollmentWithDetails extends Enrollment {
  programs: Program;
  invoices: Invoice[];
}

interface StudentWithEnrollments extends Student {
  enrollments: EnrollmentWithDetails[];
  student_parents: StudentParentRow[];
}

// ─── helpers ──────────────────────────────────────────────────────────────────

function getNextClass(enrollments: EnrollmentWithDetails[]): { program: string; date: Date } | null {
  let next: { program: string; date: Date } | null = null;
  const now = moment();
  for (const e of enrollments) {
    if (!e.is_active || !e.programs?.schedule_cron) continue;
    try {
      const { hour, minute, days } = parseCron(e.programs.schedule_cron);
      const cur = moment.max(moment(e.programs.start_date).startOf('day'), now.clone().startOf('day'));
      const end = moment(e.programs.end_date);
      while (cur.isSameOrBefore(end, 'day')) {
        const cronDay = cur.day() === 0 ? 7 : cur.day();
        if (days.includes(cronDay)) {
          const ev = cur.clone().hour(hour).minute(minute).second(0);
          if (ev.isAfter(now)) {
            if (!next || ev.isBefore(next.date)) next = { program: e.programs.name, date: ev.toDate() };
            break;
          }
        }
        cur.add(1, 'day');
      }
    } catch { /* skip malformed cron */ }
  }
  return next;
}

function getStatusColor(status: string) {
  switch (status.toLowerCase()) {
    case 'paid': return 'bg-green-100 text-green-800';
    case 'partial': return 'bg-blue-100 text-blue-800';
    case 'pending': return 'bg-yellow-100 text-yellow-800';
    case 'overdue': return 'bg-red-100 text-red-800';
    default: return 'bg-gray-100 text-gray-800';
  }
}

// ─── child check-mate sub-components ─────────────────────────────────────────

function AuthorizedPickupsSection({ studentId, studentName }: { studentId: string; studentName: string }) {
  const { data: pickups = [], isLoading } = useAuthorizedPickups(studentId);
  const addPickup = useAddAuthorizedPickup();
  const removePickup = useRemoveAuthorizedPickup();
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [relationship, setRelationship] = useState('');
  const [phone, setPhone] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);

  async function handleAdd() {
    if (!name.trim()) { setFormError('Name is required.'); return; }
    setFormError(null);
    try {
      await addPickup.mutateAsync({ studentId, studentName, name, relationship, phone });
      setName(''); setRelationship(''); setPhone(''); setShowForm(false);
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Failed to add.');
    }
  }

  // Takes the whole row, not just its id: the delete is a hard delete, so these details are
  // only still available here, and the audit event needs them.
  async function handleRemove(pickup: AuthorizedPickup) {
    setRemovingId(pickup.id);
    try {
      await removePickup.mutateAsync({
        id: pickup.id,
        studentId,
        studentName,
        name: pickup.name,
        relationship: pickup.relationship,
        phone: pickup.phone,
        photoPath: pickup.photoPath,
      });
    }
    finally { setRemovingId(null); }
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Authorized Pickups</p>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-800 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> Add
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="space-y-1.5">
          {[1, 2].map(i => <div key={i} className="h-7 bg-gray-100 animate-pulse rounded" />)}
        </div>
      ) : (
        <div className="space-y-1.5">
          {pickups.map(p => (
            <div key={p.id} className="flex items-center justify-between py-0.5">
              <div>
                <p className="text-xs font-medium text-gray-900">{p.name}</p>
                {(p.relationship || p.phone) && (
                  <p className="text-[10px] text-gray-400">
                    {[p.relationship, p.phone].filter(Boolean).join(' · ')}
                  </p>
                )}
              </div>
              <button
                onClick={() => void handleRemove(p)}
                disabled={removingId === p.id}
                className="p-1 text-gray-300 hover:text-red-400 transition-colors"
                aria-label={`Remove ${p.name}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          {pickups.length === 0 && !showForm && (
            <p className="text-xs text-gray-400 italic">No authorized pickups listed.</p>
          )}
        </div>
      )}

      {showForm && (
        <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
          {formError && <p className="text-xs text-red-600">{formError}</p>}
          <input
            type="text"
            placeholder="Name *"
            value={name}
            onChange={e => setName(e.target.value)}
            className="w-full px-2.5 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
            autoFocus
          />
          <div className="grid grid-cols-2 gap-2">
            <input
              type="text"
              placeholder="Relationship"
              value={relationship}
              onChange={e => setRelationship(e.target.value)}
              className="w-full px-2.5 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <input
              type="tel"
              placeholder="Phone"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              className="w-full px-2.5 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => void handleAdd()}
              disabled={addPickup.isPending}
              className="flex-1 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {addPickup.isPending ? 'Adding…' : 'Add Person'}
            </button>
            <button
              onClick={() => { setShowForm(false); setName(''); setRelationship(''); setPhone(''); setFormError(null); }}
              className="px-3 py-1.5 border border-gray-200 text-gray-500 text-xs rounded-lg hover:bg-gray-50 transition-colors"
            >
              <XIcon className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

interface CheckInEnrollment {
  id: string;
  program_id: string;
  metadata: { selected_days?: string[] } | null;
  programs: { name: string; schedule_cron: string; duration: number; billing_type: string } | null;
}

/**
 * What the teacher has recorded for this child today. Read-only by design.
 *
 * Parents used to have a "Check In" button here, which meant this card had to work out
 * on its own whether a session was happening — and it got that wrong for Flex families
 * (offering check-in on days they had not bought), for enrollments whose term had not
 * started, and on BHCS closures. The teacher roster is the source of truth for who is
 * expected; children walk over from school and staff record the arrival. So the card now
 * renders only what a teacher has actually asserted — arrived, or absent — and infers
 * nothing from the schedule.
 *
 * The check-out half of the record is not shown by default, here or on the dashboard: see
 * `ParentAttendancePill` for why a late or missing check-out must not change what a parent
 * reads, and for the `parentPickupDetails` flag that puts it back.
 */
export function AttendanceTodaySection({ studentId, activeEnrollments }: { studentId: string; activeEnrollments: CheckInEnrollment[] }) {
  const today = getTodaySessionDate();
  const records = useAttendanceByStudents([studentId], today).data ?? [];

  // Same builder, same filters and same pill as the dashboard's Today card. A parent who
  // taps through from one to the other must not find the day described two different ways.
  const sessions = buildTodaySessions(activeEnrollments, records);
  if (sessions.length === 0) return null;

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
      <p className={`${TYPE_SECTION} mb-3`}>Today&apos;s Attendance</p>
      <div className="space-y-2.5">
        {sessions.map(session => (
          <div key={session.enrollmentId} className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className={`${TYPE_OBJECT} truncate`}>{session.programName}</p>
              {session.timeRange && <p className={TYPE_CLOCK}>{session.timeRange}</p>}
              <ParentAttendanceTimes record={session.record} mode={session.mode} className="block" />
            </div>
            <ParentAttendancePill record={session.record} mode={session.mode} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── main component ───────────────────────────────────────────────────────────

export function Students() {
  // Invoices a bank bundle payment is still debiting: no payments row yet, but money is on its way.
  const cartHeldInvoiceIds = useCartHeldInvoiceIds();
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const t = useLabels();

  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showContactAdmin, setShowContactAdmin] = useState(false);
  const [isRemoving, setIsRemoving] = useState<string | null>(null);
  const [editingStudent, setEditingStudent] = useState<StudentWithEnrollments | null>(null);
  const [waiverPrintPreview, setWaiverPrintPreview] = useState<{ studentName: string; waiver: BhcsStudentWaiver } | null>(null);
  const [inviteStudentId, setInviteStudentId] = useState<string | null>(null);
  const [contactsEditorStudentId, setContactsEditorStudentId] = useState<string | null>(null);

  // Tab bar scroll arrow state
  const tabBarRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateArrows = useCallback(() => {
    const el = tabBarRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  }, []);

  useEffect(() => {
    const el = tabBarRef.current;
    if (!el) return;
    updateArrows();
    el.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows, { passive: true });
    return () => {
      el.removeEventListener('scroll', updateArrows);
      window.removeEventListener('resize', updateArrows);
    };
  }, [updateArrows]);

  // ─── queries ────────────────────────────────────────────────────────────────

  const { data: students, isLoading, error } = useQuery<StudentWithEnrollments[]>({
    queryKey: ['students-list'],
    queryFn: async () => {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser?.email) throw new Error('No authenticated user');

      const { data: parentData, error: parentError } = await supabase
        .from('parents').select('id').eq('email', authUser.email).single();
      if (parentError?.code === 'PGRST116') throw new Error('Parent not found');
      if (parentError) throw parentError;
      if (!parentData) throw new Error('Parent not found');

      const { data: studentParents } = await supabase
        .from('student_parents').select('student_id').eq('parent_id', parentData.id);
      const studentIds = studentParents?.map(sp => sp.student_id) || [];

      const { data, error } = await supabase
        .from('students')
        .select(`
          id, first_name, last_name, date_of_birth, metadata, active, created_at, updated_at,
          added_by(id, first_name, last_name),
          student_parents(
            parent_id, relationship, is_primary, can_pay,
            parents(id, first_name, last_name, email)
          ),
          enrollments(
            id, student_id, program_id, created_at, updated_at, is_active, metadata,
            programs(
              id, name, type, price, billing_type, brand_key, active,
              start_date, end_date, schedule_cron, duration, created_at, updated_at
            ),
            invoices(id, total_amount, amount_paid, status, due_date)
          )
        `)
        .in('id', studentIds)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as unknown as StudentWithEnrollments[];
    },
    retry: (count, err) => err.message === 'Parent not found' ? false : count < 3,
  });

  const { data: currentParent } = useQuery({
    queryKey: ['current-parent'],
    queryFn: async () => {
      if (!user?.email) return null;
      const { data } = await supabase.from('parents').select('id').eq('email', user.email).single();
      return data;
    },
    enabled: !!user?.email,
  });

  // ─── selection init ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (error?.message === 'Parent not found') navigate('/dashboard', { replace: true });
  }, [error, navigate]);

  useEffect(() => {
    if (!students) return;
    setSelectedStudentId(prev => {
      // Honor navigation state (e.g. Dashboard co-parent invite nudge)
      const state = location.state as { openInviteForStudentId?: string } | null;
      if (state?.openInviteForStudentId) {
        const found = students.find(s => s.id === state.openInviteForStudentId);
        if (found) {
          setInviteStudentId(state.openInviteForStudentId);
          return state.openInviteForStudentId;
        }
      }
      // Keep existing selection if still valid
      if (prev && students.find(s => s.id === prev)) return prev;
      return students[0]?.id ?? null;
    });
  }, [students, location.state]);

  // ─── derived ─────────────────────────────────────────────────────────────────

  const student = useMemo(
    () => students?.find(s => s.id === selectedStudentId) ?? null,
    [students, selectedStudentId],
  );

  const waiverStatus = useMemo(() => {
    if (!student) return null;
    const raw = student.metadata?.bhcs_waiver;
    if (isBhcsStudentWaiverSigned(raw)) return { status: 'signed' as const, waiver: raw };
    if (isBhcsStudentWaiverOptOut(raw)) return { status: 'opt-out' as const };
    return { status: 'not-signed' as const };
  }, [student]);

  const waiverContacts = useMemo(
    () => readWaiverContacts(student?.metadata?.bhcs_waiver),
    [student],
  );

  const activeEnrollments = useMemo(
    () => (student?.enrollments ?? []).filter(e => e.is_active),
    [student],
  );

  const hasInactiveEnrollments = useMemo(
    () => (student?.enrollments ?? []).some(e => !e.is_active),
    [student],
  );

  const nextClass = useMemo(
    () => (student?.active ? getNextClass(student.enrollments ?? []) : null),
    [student],
  );

  const gradeLabel = useMemo(() => {
    const v = student?.metadata?.grade;
    if (!v) return null;
    return grades.find(g => g.value === v)?.label ?? v;
  }, [student]);

  // A co-parent we cannot read is dropped rather than rendered: the link row alone has no
  // name to show, and there is nothing useful to say about a family member we can't name.
  const otherParents = useMemo(() => {
    const rows = student?.student_parents ?? [];
    const named = rows.filter(
      (sp): sp is StudentParentRow & { parents: ParentInfo } => sp.parents != null
    );
    if (!currentParent) return named;
    return named.filter(sp => sp.parent_id !== currentParent.id);
  }, [student, currentParent]);

  // ─── handlers ────────────────────────────────────────────────────────────────

  const handleAddStudentClick = () => {
    if (settings.allowParentsToAddStudents) {
      trackEvent({ eventType: 'student_create', user, userType: 'parent', entityType: 'student', metadata: { action: 'modal_opened', source: 'students_page' } });
      setShowAddForm(true);
    } else {
      setShowContactAdmin(true);
    }
  };

  const handleRemoveStudent = async (studentId: string, studentName: string) => {
    setIsRemoving(studentId);
    try {
      const { data: deleted, error: deleteError } = await supabase
        .from('students').delete().eq('id', studentId).select('id');
      if (deleteError) throw deleteError;
      if (!deleted?.length) throw new Error('Unable to remove student.');
      await queryClient.invalidateQueries({ queryKey: ['students-list'] });
      trackEvent({ eventType: 'student_delete', user, entityType: 'student', entityId: studentId, metadata: { student_name: studentName } });
    } catch (err) {
      console.error('Failed to remove student:', err);
    } finally {
      setIsRemoving(null);
    }
  };

  // ─── early returns ────────────────────────────────────────────────────────────

  if (authLoading) return <div data-testid="loading-skeleton">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (isLoading) return <div data-testid="loading-skeleton">Loading students...</div>;

  if (error) {
    return (
      <div className="p-4">
        <h1 className="text-xl font-bold mb-4">My Students</h1>
        <div className="bg-white p-6 rounded-lg shadow">
          <p className="text-red-600">Error loading students. Please try again later.</p>
        </div>
      </div>
    );
  }

  if (waiverPrintPreview) {
    return (
      <WaiverPrintPreview
        studentName={waiverPrintPreview.studentName}
        waiver={waiverPrintPreview.waiver}
        onBack={() => setWaiverPrintPreview(null)}
      />
    );
  }

  // ─── render ───────────────────────────────────────────────────────────────────

  return (
    <div data-testid="students-page" className="p-3 sm:p-4">

      {/* ── Modals ─────────────────────────────────────────────────────────── */}
      {showAddForm && settings.allowParentsToAddStudents && (
        <ChildForm onClose={() => setShowAddForm(false)} />
      )}

      {showContactAdmin && !settings.allowParentsToAddStudents && (
        <div className="fixed inset-0 z-[60] flex flex-col justify-end sm:items-center sm:justify-center">
          <div className="absolute inset-0 bg-gray-500/75" onClick={() => setShowContactAdmin(false)} aria-hidden="true" />
          <div className="relative w-full bg-white rounded-t-2xl shadow-xl sm:rounded-lg sm:max-w-md sm:my-8 flex flex-col max-h-[90vh]">
            <div className="flex justify-center pt-3 pb-1 sm:hidden" aria-hidden="true">
              <div className="w-10 h-1 rounded-full bg-gray-300" />
            </div>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
              <h3 className="text-base font-semibold text-gray-900">Contact Administrator</h3>
              <button onClick={() => setShowContactAdmin(false)} className="text-gray-500 hover:text-gray-700 p-1.5 rounded" aria-label="Close">
                <span aria-hidden className="text-xl leading-none">×</span>
              </button>
            </div>
            <div className="overflow-y-auto p-5" style={{ paddingBottom: 'max(1.25rem, env(safe-area-inset-bottom, 0px))' }}>
              <div className="flex items-start gap-3 mb-5">
                <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-gray-600">
                  To add a new student to BHCS, please reach out to our billing department at{' '}
                  <a href={`mailto:${settings.supportEmail}`} className="text-blue-600 hover:text-blue-800">{settings.supportEmail}</a>
                </p>
              </div>
              <div className="flex justify-end">
                <button onClick={() => setShowContactAdmin(false)} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {editingStudent && (
        <ChildForm student={editingStudent} isEdit onClose={() => setEditingStudent(null)} />
      )}

      {inviteStudentId && students && (() => {
        const s = students.find(st => st.id === inviteStudentId);
        if (!s) return null;
        return <CoParentInviteModal studentId={inviteStudentId} studentFirstName={s.first_name} onClose={() => setInviteStudentId(null)} />;
      })()}

      {contactsEditorStudentId && students && (() => {
        const s = students.find(st => st.id === contactsEditorStudentId);
        if (!s) return null;
        return (
          <StudentContactsEditor
            studentId={s.id}
            studentName={`${s.first_name} ${s.last_name}`}
            waiver={(s.metadata?.bhcs_waiver ?? null) as BhcsWaiverMetadata | null}
            user={user}
            userType="parent"
            onClose={() => setContactsEditorStudentId(null)}
            onSaved={() => {
              queryClient.invalidateQueries({ queryKey: ['students-list'] });
            }}
          />
        );
      })()}

      {/* ── Student tab selector ──────────────────────────────────────────── */}
      <div className="relative mb-4 sm:mb-5">
        {/* Left fade + arrow */}
        {canScrollLeft && (
          <>
            <div className="pointer-events-none absolute inset-y-0 left-0 w-10 bg-gradient-to-r from-gray-50 to-transparent z-10" />
            <button onClick={() => tabBarRef.current?.scrollBy({ left: -160, behavior: 'smooth' })}
              className="absolute inset-y-0 left-0 z-20 flex items-center px-0.5 text-gray-400 hover:text-gray-600" aria-label="Scroll left">
              <ChevronLeft className="w-4 h-4" />
            </button>
          </>
        )}

        <div ref={tabBarRef} className="flex items-center gap-2 overflow-x-auto no-scrollbar">
          {/* Student tabs */}
          {(students ?? []).map(s => {
            const isSelected = s.id === selectedStudentId;
            const color = solidTintFor(`${s.first_name} ${s.last_name}`);
            const initials = `${s.first_name[0] ?? ''}${s.last_name[0] ?? ''}`.toUpperCase();
            return (
              <button
                key={s.id}
                onClick={() => setSelectedStudentId(s.id)}
                className={`flex-shrink-0 flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-all ${
                  isSelected
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'bg-white text-gray-700 border border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0 ${
                  isSelected ? 'bg-blue-400' : color
                }`}>
                  {initials}
                </span>
                <span>{s.first_name}</span>
                {!s.active && <span className={`text-[10px] px-1 rounded-full ${isSelected ? 'bg-blue-400/50' : 'bg-gray-200 text-gray-500'}`}>pending</span>}
              </button>
            );
          })}

          {/* Add student */}
          <button
            onClick={handleAddStudentClick}
            className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium text-blue-600 border border-dashed border-blue-300 hover:bg-blue-50 transition-colors"
          >
            <UserPlus className="w-3.5 h-3.5" />
            <span>{t('action.addStudent')}</span>
          </button>
        </div>

        {/* Right fade + arrow */}
        {canScrollRight && (
          <>
            <div className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-gray-50 to-transparent z-10" />
            <button onClick={() => tabBarRef.current?.scrollBy({ left: 160, behavior: 'smooth' })}
              className="absolute inset-y-0 right-0 z-20 flex items-center px-0.5 text-gray-400 hover:text-gray-600" aria-label="Scroll right">
              <ChevronRight className="w-4 h-4" />
            </button>
          </>
        )}
      </div>

      {/* ── Empty state ───────────────────────────────────────────────────── */}
      {(!students || students.length === 0) && (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 text-sm mb-4">No students yet. Add your first student to get started.</p>
          <button onClick={handleAddStudentClick}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors">
            <UserPlus className="w-4 h-4" />
            Add New Student
          </button>
        </div>
      )}

      {/* ── Student detail ────────────────────────────────────────────────── */}
      {student && (
        (() => {
          const avatarColor = solidTintFor(`${student.first_name} ${student.last_name}`);
          const initials = `${student.first_name[0] ?? ''}${student.last_name[0] ?? ''}`.toUpperCase();
          const age = student.date_of_birth ? calculateAge(student.date_of_birth) : null;
          const signedWaiver = waiverStatus?.status === 'signed' ? waiverStatus.waiver : null;

          return (
            <div
              data-testid="student-card"
              className="grid grid-cols-1 lg:grid-cols-3 gap-4"
            >
              {/* ── Left column ──────────────────────────────────────────── */}
              <div className="space-y-4">

                {/* Profile card */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                  {/* Avatar + edit */}
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-14 h-14 rounded-full ${avatarColor} flex items-center justify-center text-white font-bold text-xl`}>
                      {initials}
                    </div>
                    <button
                      onClick={() => setEditingStudent(student)}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                      aria-label={`Edit ${student.first_name}`}
                    >
                      <Edit className="w-3.5 h-3.5" />
                      {t('action.edit')}
                    </button>
                  </div>

                  {/* Name */}
                  <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                    {student.first_name} {student.last_name}
                  </h2>

                  {!student.active && (
                    <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                      Pending Approval
                    </span>
                  )}

                  {typeof student.metadata?.deposit_badge?.amount === 'number' && student.metadata.deposit_badge.amount > 0 && (
                    <span className="inline-flex items-center gap-1 mt-2 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
                      💰 Deposit ${student.metadata.deposit_badge.amount.toFixed(2)}
                    </span>
                  )}

                  {/* Info rows */}
                  <dl className="mt-3 space-y-2">
                    {age !== null && student.date_of_birth && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <span className="text-base">🎂</span>
                        <span>
                          {moment(student.date_of_birth).format('MMM D, YYYY')}
                          <span className="text-gray-400 ml-1">({age} years old)</span>
                        </span>
                      </div>
                    )}
                    {gradeLabel && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <GraduationCap className="w-4 h-4 text-gray-400" />
                        <span>{gradeLabel}</span>
                      </div>
                    )}
                    {student.metadata?.school && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <School className="w-4 h-4 text-gray-400" />
                        <span>{student.metadata.school}</span>
                      </div>
                    )}
                    {student.metadata?.room_number && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <DoorOpen className="w-4 h-4 text-gray-400" />
                        <span>Room {student.metadata.room_number}</span>
                      </div>
                    )}
                  </dl>

                  {/* Waiver */}
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">{t('header.waiver')}</p>
                    {waiverStatus?.status === 'not-signed' && (
                      <Link to="/student-waiver"
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-yellow-500 text-white hover:bg-yellow-600 transition-colors">
                        <AlertCircle className="w-3.5 h-3.5" />
                        Signature required
                      </Link>
                    )}
                    {waiverStatus?.status === 'signed' && (
                      <div className="space-y-1">
                        <button
                          type="button"
                          onClick={() => setWaiverPrintPreview({ studentName: `${student.first_name} ${student.last_name}`, waiver: waiverStatus.waiver })}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-green-100 text-green-800 border border-green-200 hover:bg-green-200 transition-colors"
                        >
                          <FileText className="w-3.5 h-3.5" />
                          Signed · {moment(waiverStatus.waiver.signature.date).format('MMM D, YYYY')}
                        </button>
                      </div>
                    )}
                    {waiverStatus?.status === 'opt-out' && (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-gray-100 text-gray-600">
                        Waiver opted out
                      </span>
                    )}
                    {waiverStatus?.status !== 'not-signed' && (
                      <button
                        type="button"
                        onClick={() => setContactsEditorStudentId(student.id)}
                        className="mt-1.5 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 hover:bg-blue-100 transition-colors"
                      >
                        <Edit className="w-3.5 h-3.5" />
                        Update contacts
                      </button>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap gap-2">
                    {student.active && (
                      <button
                        onClick={() => navigate('/programs')}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                      >
                        <UserPlus className="w-3.5 h-3.5" />
                        {t('action.enrollShort')}
                      </button>
                    )}
                    {(student.enrollments ?? []).length === 0 && (
                      <button
                        onClick={() => handleRemoveStudent(student.id, `${student.first_name} ${student.last_name}`)}
                        disabled={isRemoving === student.id}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 border border-red-200 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        {isRemoving === student.id ? 'Removing…' : 'Remove'}
                      </button>
                    )}
                  </div>
                </div>

                {/* Emergency contact. Read shape-agnostically rather than from
                    signedWaiver: contacts are editable on opt-out rows too, and
                    those never satisfy isBhcsStudentWaiverSigned. */}
                {waiverContacts.emergencyContact && (
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">{t('header.emergencyContact')}</p>
                    <div className="space-y-1.5">
                      <p className="text-sm font-medium text-gray-900">{waiverContacts.emergencyContact.name}</p>
                      {waiverContacts.emergencyContact.relationship && (
                        <p className="text-xs text-gray-500">{waiverContacts.emergencyContact.relationship}</p>
                      )}
                      {waiverContacts.emergencyContact.phone && (
                        <a href={`tel:${waiverContacts.emergencyContact.phone}`}
                          className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800">
                          <Phone className="w-3.5 h-3.5" />
                          {waiverContacts.emergencyContact.phone}
                        </a>
                      )}
                    </div>

                    {signedWaiver?.hasAllergies && signedWaiver.allergiesDetails && (
                      <div className="mt-3 bg-orange-50 border border-orange-100 rounded-lg p-2.5">
                        <p className="text-xs font-semibold text-orange-800 mb-1">⚠️ {t('header.allergies')}</p>
                        <p className="text-xs text-orange-700">{signedWaiver.allergiesDetails}</p>
                      </div>
                    )}

                    {signedWaiver?.physician?.name && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">{t('header.physician')}</p>
                        <p className="text-sm text-gray-700">{signedWaiver.physician.name}</p>
                        {signedWaiver.physician.phone && (
                          <a href={`tel:${signedWaiver.physician.phone}`} className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 mt-0.5">
                            <Phone className="w-3 h-3" />
                            {signedWaiver.physician.phone}
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Family access */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{t('header.familyAccess')}</p>
                    {student.active && (
                      <button
                        onClick={() => setInviteStudentId(student.id)}
                        className="flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-800 transition-colors"
                      >
                        <Users className="w-3.5 h-3.5" />
                        {t('action.inviteShort')}
                      </button>
                    )}
                  </div>

                  {/* Current parent (me) */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between py-1">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center text-[10px] font-bold text-blue-600">
                          Me
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-900">You</p>
                          <p className="text-[10px] text-gray-400">
                            {(student.student_parents ?? []).find(sp => sp.parent_id === currentParent?.id)?.relationship ?? 'Parent'}
                          </p>
                        </div>
                      </div>
                      {(student.student_parents ?? []).find(sp => sp.parent_id === currentParent?.id)?.is_primary && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium">Primary</span>
                      )}
                    </div>

                    {otherParents.map(sp => (
                      <div key={sp.parent_id} className="flex items-center justify-between py-1">
                        <div className="flex items-center gap-2">
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${solidTintFor(`${sp.parents.first_name} ${sp.parents.last_name}`)}`}>
                            {(sp.parents.first_name[0] ?? '').toUpperCase()}{(sp.parents.last_name[0] ?? '').toUpperCase()}
                          </div>
                          <div>
                            <p className="text-xs font-medium text-gray-900">{sp.parents.first_name} {sp.parents.last_name}</p>
                            <p className="text-[10px] text-gray-400">{sp.relationship}</p>
                          </div>
                        </div>
                        {sp.is_primary && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium">Primary</span>
                        )}
                      </div>
                    ))}

                    {otherParents.length === 0 && (
                      <p className="text-xs text-gray-400 italic">No co-parent linked yet.</p>
                    )}
                  </div>
                </div>

                {/* Authorized pickups */}
                {student.active && (
                  <AuthorizedPickupsSection
                    studentId={student.id}
                    studentName={`${student.first_name} ${student.last_name}`.trim()}
                  />
                )}

              </div>

              {/* ── Right column ──────────────────────────────────────────── */}
              <div className="lg:col-span-2 space-y-4">

                {/* Pending state overlay */}
                {!student.active && (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                    <div className="flex gap-3">
                      <span className="text-2xl">✨</span>
                      <div>
                        <h3 className="text-base font-semibold text-blue-800 mb-1">Almost there!</h3>
                        <p className="text-sm text-blue-700 mb-3">
                          {student.first_name}'s account is pending admin approval. You'll get an email once they're ready to enroll.
                        </p>
                        <ul className="text-sm text-blue-700 space-y-1.5">
                          <li className="flex items-center gap-2"><span>✅</span> Student info submitted</li>
                          <li className="flex items-center gap-2"><span>👀</span> Admin team reviewing</li>
                          <li className="flex items-center gap-2"><span>📧</span> Email confirmation within 1–2 business days</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {/* Active programs */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                    <p className="text-sm font-semibold text-gray-900">{t('header.activePrograms')}</p>
                    {hasInactiveEnrollments && (
                      <Link to="/enrollment-history" state={{ selectedStudentIds: [student.id] }}
                        className="text-xs text-blue-600 hover:text-blue-800 hover:underline" aria-label={`Enrollment history for ${student.first_name}`}>
                        {t('label.history')} →
                      </Link>
                    )}
                  </div>

                  {activeEnrollments.length === 0 ? (
                    <div className="px-4 py-6 text-center">
                      <p className="text-sm text-gray-400">No active programs.</p>
                      {student.active && (
                        <button onClick={() => navigate('/programs')}
                          className="mt-3 inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 font-medium">
                          <UserPlus className="w-3.5 h-3.5" /> Browse programs →
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {activeEnrollments.map(enrollment => {
                        const paymentStatus = getEnrollmentPaymentStatus(enrollment, enrollment.invoices, cartHeldInvoiceIds);
                        const schedule = enrollment.programs?.schedule_cron ? getScheduleTimeRange(enrollment.programs.schedule_cron) : null;
                        return (
                          <Link
                            key={enrollment.id}
                            to={`/enrollments/${enrollment.id}`}
                            className="flex items-center gap-3 px-4 py-3.5 hover:bg-gray-50 transition-colors"
                            aria-label={`View enrollment in ${enrollment.programs?.name}`}
                            style={{ textDecoration: 'none', color: 'inherit' }}
                          >
                            <span className="text-xl flex-shrink-0">{getProgramTypeIcon(enrollment.programs?.type)}</span>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{enrollment.programs?.name}</p>
                              {/* The one list in the portal that deliberately mixes brands: a
                                  child's programs are shown whichever profile you are in, so
                                  each row has to say whose class it is. */}
                              <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                                <BrandBadge brand={enrollment.programs?.brand_key} size="xs" />
                                <p className="text-xs text-gray-500">
                                  {formatDateDisplay(enrollment.programs?.start_date)} – {formatDateDisplay(enrollment.programs?.end_date)}
                                  {schedule && <span className="ml-1.5 text-gray-400">· {schedule}</span>}
                                </p>
                              </div>
                            </div>
                            <span className={`flex-shrink-0 px-2 py-0.5 rounded-full text-[10px] font-medium ${getStatusColor(paymentStatus)}`}>
                              {paymentStatus}
                            </span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Next class */}
                {nextClass && (
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3.5 flex items-center gap-3">
                    <CalendarClock className="w-5 h-5 text-blue-500 flex-shrink-0" />
                    <div>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-0.5">{t('label.nextClass')}</p>
                      <p className="text-sm text-gray-900">
                        <span className="font-medium">{nextClass.program}</span>
                        <span className="text-gray-400 mx-1.5">·</span>
                        {moment(nextClass.date).format('ddd, MMM D')} at {moment(nextClass.date).format('h:mm A')}
                      </p>
                    </div>
                  </div>
                )}

                {/* Today's attendance, as recorded by the teacher */}
                {student.active && (
                  <AttendanceTodaySection
                    studentId={student.id}
                    activeEnrollments={activeEnrollments as CheckInEnrollment[]}
                  />
                )}

              </div>
            </div>
          );
        })()
      )}
    </div>
  );
}
